import { fromPairs } from "lodash";
import { v4 } from "uuid";

import { AgentNodeForm, SchemaError } from "src/agentNode/types";
import {
  buildFieldNameToIdMap,
  validateInputSchemaMapping,
} from "src/parentFlowNodes/utils";
import { SchemaUI } from "src/schema/schemaMappingUtils";

type UISchemas = { input?: SchemaUI };

export type FieldToRemove = `input_mappings.${string}`;

/**
 * Sets up the agent node form by merging existing mappings with current schema.
 * Creates new mapping entries for new schema fields, preserves existing values,
 * and identifies fields to remove that are no longer in the schema.
 */
export const setupAgentNodeForm = (
  data: AgentNodeForm,
  schemas: UISchemas,
): [AgentNodeForm, SchemaError[], FieldToRemove[]] => {
  const inputMappings = data.input_mappings ?? {};

  // Build input mappings from schema
  const inputFieldNameToId = buildFieldNameToIdMap(inputMappings);
  const mergedInputMappings = fromPairs(
    (schemas.input?.properties ?? []).map((field) => {
      const id = inputFieldNameToId[field.fieldName] ?? v4();

      return [
        id,
        {
          id,
          source: inputMappings[id]?.source ?? "",
          destination: field.fieldName,
        },
      ];
    }),
  );
  const inputFieldsToRemove = Object.keys(inputMappings)
    .filter((fieldId) => !mergedInputMappings[fieldId])
    .map((fieldId) => `input_mappings.${fieldId}` as FieldToRemove);

  const updatedData: AgentNodeForm = {
    ...data,
    input_mappings: mergedInputMappings,
  };

  return [
    updatedData,
    validateInputSchemaMapping(schemas.input, mergedInputMappings),
    inputFieldsToRemove,
  ];
};
